import MainLayout from "../../layout/MainLayout";
import { Button2, Checkbox, QuickActionSelect } from "../index";
import { Input } from "antd";
import searchIcon from "../../assets/icons/search-icon.png";
import plusIcon from "../../assets/icons/plus-circle-white.png";
import { TfiDownload } from "react-icons/tfi";
import { FiEdit2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import Table from "../table/Table";
import ijCorpIcon from "../../assets/temp-icons/ijcorp-icon.png";
import { useBeneficiaries } from "../../data/live";
import { beneficiariesApi } from "../../api/endpoints";

const Recipient = () => {
  const navigate = useNavigate();
  const { beneficiaries, refresh, source } = useBeneficiaries();
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("recipientName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [hiddenRecipientIds, setHiddenRecipientIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("deletedRecipientIds") || "[]");
    } catch {
      return [];
    }
  });

  const liveTableBody = useMemo(() => beneficiaries.map((b) => {
    const bank = b.bankAccounts?.[0];
    const wallet = b.mobileWallets?.[0];
    const method =
      b.payoutMethod === "BANK_ACCOUNT"
        ? "Bank"
        : b.payoutMethod === "MOBILE_WALLET"
        ? "Mobile Wallet"
        : "Cash Pickup";
    return {
      id: b.id || `${b.fullName}-${b.countryCode}`,
      recipientData: {
        icon: ijCorpIcon,
        recipientName: b.fullName,
      },
      category: "-",
      tags: ["-"],
      purpose: "-",
      method,
      currency: bank?.currency || wallet?.currency || "-",
      type: b.payoutMethod,
      country: b.countryCode || "-",
      identifier: bank?.iban || bank?.accountNumber || wallet?.walletId || "-",
      raw: b,
    };
  }).filter((row) => !hiddenRecipientIds.includes(row.id)), [beneficiaries, hiddenRecipientIds]);

  const filteredRecipients = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return liveTableBody;

    return liveTableBody.filter((r) =>
      [
        r.recipientData?.recipientName,
        r.category,
        ...(Array.isArray(r.tags) ? r.tags : []),
        r.purpose,
        r.method,
        r.currency,
        r.type,
        r.country,
        r.identifier,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [liveTableBody, search]);

  const sortedRecipients = useMemo(() => {
    const getValue = (row) => {
      if (sortKey === "recipientName") return row.recipientData?.recipientName || "";
      return row[sortKey] || "";
    };

    return [...filteredRecipients].sort((a, b) => {
      const first = String(getValue(a)).toLowerCase();
      const second = String(getValue(b)).toLowerCase();
      const result = first.localeCompare(second, undefined, { numeric: true });
      return sortDirection === "asc" ? result : -result;
    });
  }, [filteredRecipients, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedRecipients.length / pageSize));

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortKey, sortDirection, pageSize]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const paginatedRecipients = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedRecipients.slice(start, start + pageSize);
  }, [currentPage, pageSize, sortedRecipients]);

  const firstResult = sortedRecipients.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastResult = Math.min(currentPage * pageSize, sortedRecipients.length);
  const selectableIds = sortedRecipients.map((row) => row.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return prev.filter((id) => !selectableIds.includes(id));
      return [...new Set([...prev, ...selectableIds])];
    });
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectedRecipients = sortedRecipients.filter((row) =>
    selectedIds.includes(row.id)
  );

  const deleteSelected = async () => {
    if (selectedIds.length === 0 || deleting) return;
    const confirmed = window.confirm(
      `Delete ${selectedIds.length} selected recipient${selectedIds.length > 1 ? "s" : ""}?`
    );
    if (!confirmed) return;

    setDeleting(true);
    setDeleteError("");
    try {
      if (source === "api") {
        await Promise.all(selectedIds.map((id) => beneficiariesApi.remove(id)));
        await refresh();
      } else {
        const nextHiddenIds = [...new Set([...hiddenRecipientIds, ...selectedIds])];
        localStorage.setItem("deletedRecipientIds", JSON.stringify(nextHiddenIds));
        setHiddenRecipientIds(nextHiddenIds);
      }
      setSelectedIds([]);
    } catch (err) {
      setDeleteError(
        err.response?.data?.error || err.message || "Unable to delete selected recipients"
      );
    } finally {
      setDeleting(false);
    }
  };

  const editSelected = () => {
    if (selectedRecipients.length !== 1) return;
    navigate("/add-recipient", {
      state: { recipient: selectedRecipients[0].raw },
    });
  };

  const exportRecipients = () => {
    const rows = selectedRecipients.length > 0 ? selectedRecipients : sortedRecipients;
    const headers = [
      "Recipient Name",
      "Category",
      "Tags",
      "Purpose",
      "Method",
      "Currency",
      "Type",
      "Country",
      "Identifier",
    ];
    const csvRows = rows.map((row) => [
      row.recipientData?.recipientName || "",
      row.category || "",
      Array.isArray(row.tags) ? row.tags.join("; ") : "",
      row.purpose || "",
      row.method || "",
      row.currency || "",
      row.type || "",
      row.country || "",
      row.identifier || "",
    ]);
    const csv = [headers, ...csvRows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = selectedRecipients.length > 0 ? "selected-recipients.csv" : "recipients.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleHeaderSort = (key) => {
    setSortKey(key);
    setSortDirection((direction) =>
      sortKey === key && direction === "asc" ? "desc" : "asc"
    );
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return "↕";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const sortableHeader = (label, key) => (
    <button
      type="button"
      className="recipient-table-sort"
      onClick={() => handleHeaderSort(key)}
    >
      <span>{label}</span>
      <span>{sortIcon(key)}</span>
    </button>
  );

  const tableHeaders = [
    <Checkbox
      checked={allSelected}
      indeterminate={someSelected}
      onChange={toggleAll}
    />,
    sortableHeader("Recipient Name", "recipientName"),
    sortableHeader("Category", "category"),
    "Tags",
    sortableHeader("Purpose", "purpose"),
    sortableHeader("Method", "method"),
    sortableHeader("Currency", "currency"),
    sortableHeader("Type", "type"),
    sortableHeader("Country", "country"),
    sortableHeader("Identifier", "identifier"),
  ];


  return (
    <>
      <MainLayout
        expand={false}
        NavName="Recipients"
        navbarChildren={<QuickActionSelect />}
        pageClassName="dashboard-page recipient-page"
        contentClassName="dashboard-content"
      >
        <div className="recipient-shell">
          <div className="recipient-overview">
            <div>
              <span className="dashboard-eyebrow">Beneficiary directory</span>
              <h2>Recipients List</h2>
              <p>Search, sort, and manage saved recipients for local and international transfers.</p>
            </div>
            <Button2
              buttonName={"New Recipients"}
              onClick={() => navigate("/add-recipient")}
              className={"btn-dark"}
              divClassName={"gap-1"}
              icon={<img src={plusIcon} alt="add" />}
            />
          </div>

          <div className="recipient-card mt-4">
              <div className="recipient-toolbar d-flex justify-content-between flex-column flex-xl-row p-3 p-md-4 py-md-3 align-items-xl-center gap-3">
                <div>
                  {/* <span className="dashboard-eyebrow">Data table</span> */}
                  <h4>Recipients</h4>
                </div>
                <div className="recipient-controls d-flex gap-2 align-items-center justify-content-start justify-content-xl-end flex-wrap">
                  <Input
                    placeholder="Search recipients"
                    className="recipient-search rounded-5 py-2"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    prefix={<img src={searchIcon} alt="search" className="sidebar-img" />}
                  />

                  {selectedRecipients.length === 1 && (
                    <Button2
                      buttonName="Edit"
                      className={"btn-outline-success"}
                      divClassName={"gap-1"}
                      icon={<FiEdit2 className="fs-6" />}
                      onClick={editSelected}
                    />
                  )}

                  {selectedIds.length > 0 && (
                    <button
                      type="button"
                      className="recipient-delete-button"
                      onClick={deleteSelected}
                    >
                      {deleting ? "Deleting..." : `Delete (${selectedIds.length})`}
                    </button>
                  )}

                  {deleteError && (
                    <span className="fs12px" style={{ color: "#FC555B" }}>{deleteError}</span>
                  )}

                  <Button2
                    buttonName={selectedIds.length > 0 ? "Export Selected" : "Export"}
                    className={"btn-outline-success"}
                    divClassName={"gap-1"}
                    icon={<TfiDownload className="fs-6" />}
                    onClick={exportRecipients}
                  />
                </div>
              </div>

              <div className="recipient-table-wrap p-3 p-md-4 pt-md-3">
                <Table
                  className={"w-100"}
                  tableBody={paginatedRecipients.map((row) => ({
                    ...row,
                    selected: selectedIds.includes(row.id),
                    onToggle: () => toggleOne(row.id),
                  }))}
                  tableHeaders={tableHeaders}
                  keyNumber1={1}
                  keyNumber2={3}
                  condition1={"col-3"}
                  condition2={""}
                  RecipientsTable={true}
                />

                <div className="recipient-pagination">
                  <span>
                    Showing {firstResult}-{lastResult} of {sortedRecipients.length}
                  </span>
                  <div className="d-flex align-items-center gap-2">
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      aria-label="Rows per page"
                    >
                      <option value={5}>5 / page</option>
                      <option value={10}>10 / page</option>
                      <option value={15}>15 / page</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <strong>
                      {currentPage} / {totalPages}
                    </strong>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </MainLayout>
    </>
  );
};

export default Recipient;





